import { AfterViewChecked, Component, ElementRef, ViewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faCheck, faEdit } from '@fortawesome/free-solid-svg-icons'
import { WjChartModule } from '@grapecity/wijmo.angular2.chart'

import { BaseComponent, ColumnChart, FeatureType, Grid } from '../../shared'

interface Metrics {
    bidsWonThisQuarter: string
    bidsWonLastQuarter: string
    bidsWonRolling12Months: string
    nextYearRevenueBacklog: string
    nextYearBacklogYear: number
    followingYearRevenueBacklog: string
    followingYearBacklogYear: number
    nextYearRevenueGoal: string
    nextYear: number
    followingYearRevenueGoal: string
    followingYear: number
    currentYearRevenueGoal: string
    currentYear: number
    currentYearRevenueBacklog: string
    currentQuarterGoal: string
}

interface BidMetricUpdateDto {
    name: string
    value: string
}

@Component({
    templateUrl: './bid-dashboard.component.html',
    standalone: true,
    imports: [WjChartModule, FormsModule, FaIconComponent],
})
export class BidDashboardComponent extends BaseComponent implements AfterViewChecked {
    faEdit = faEdit
    faCheck = faCheck

    grid: Grid<any>
    chartData!: ColumnChart
    chartData2!: ColumnChart
    metrics: Metrics = {
        bidsWonThisQuarter: '',
        bidsWonLastQuarter: '',
        bidsWonRolling12Months: '',
        nextYearRevenueBacklog: '',
        nextYearBacklogYear: 0,
        followingYearRevenueBacklog: '',
        followingYearBacklogYear: 0,
        nextYearRevenueGoal: '',
        nextYear: 0,
        followingYearRevenueGoal: '',
        followingYear: 0,
        currentYearRevenueGoal: '',
        currentYear: 0,
        currentYearRevenueBacklog: '',
        currentQuarterGoal: '',
    }

    editingNextYearRevenueGoal = false
    editingFollowingYearRevenueGoal = false
    editingCurrentYearRevenueGoal = false
    editingCurrentQuarterGoal = false
    nextYearRevenueGoalEditValue = ''
    followingYearRevenueGoalEditValue = ''
    currentYearRevenueGoalEditValue = ''
    currentQuarterGoalEditValue = ''
    private shouldFocusNextYearRevenueGoal = false
    private shouldFocusFollowingYearRevenueGoal = false
    private shouldFocusCurrentYearRevenueGoal = false
    private shouldFocusCurrentQuarterGoal = false

    @ViewChild('nextYearRevenueGoalInput') nextYearRevenueGoalInput!: ElementRef<HTMLInputElement>
    @ViewChild('followingYearRevenueGoalInput') followingYearRevenueGoalInput!: ElementRef<HTMLInputElement>
    @ViewChild('currentYearRevenueGoalInput') currentYearRevenueGoalInput!: ElementRef<HTMLInputElement>
    @ViewChild('currentQuarterGoalInput') currentQuarterGoalInput!: ElementRef<HTMLInputElement>

    constructor() {
        super()
        this.grid = new Grid<any>(this.services).filterBy(item => [item.id])
        this.refresh()
    }

    public refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.get('/api/BidDashboard/GetProjections'),
                    this.services.data.get('/api/BidDashboard/GetContractDollars'),
                    this.services.data.get('/api/BidDashboard/GetMetrics'),
                )
                .subscribe({
                    next: results => {
                        this.initialLoadingComplete()
                        this.chartData = results[0]
                        this.chartData2 = results[1]
                        this.metrics = results[2]
                    },
                }),
        )
    }

    public get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.BidAnalyticsView])
    }

    ngAfterViewChecked() {
        if (this.shouldFocusNextYearRevenueGoal && this.nextYearRevenueGoalInput) {
            this.nextYearRevenueGoalInput.nativeElement.focus()
            this.nextYearRevenueGoalInput.nativeElement.select()
            this.shouldFocusNextYearRevenueGoal = false
        }
        if (this.shouldFocusFollowingYearRevenueGoal && this.followingYearRevenueGoalInput) {
            this.followingYearRevenueGoalInput.nativeElement.focus()
            this.followingYearRevenueGoalInput.nativeElement.select()
            this.shouldFocusFollowingYearRevenueGoal = false
        }
        if (this.shouldFocusCurrentYearRevenueGoal && this.currentYearRevenueGoalInput) {
            this.currentYearRevenueGoalInput.nativeElement.focus()
            this.currentYearRevenueGoalInput.nativeElement.select()
            this.shouldFocusCurrentYearRevenueGoal = false
        }
        if (this.shouldFocusCurrentQuarterGoal && this.currentQuarterGoalInput) {
            this.currentQuarterGoalInput.nativeElement.focus()
            this.currentQuarterGoalInput.nativeElement.select()
            this.shouldFocusCurrentQuarterGoal = false
        }
    }

    public startEditNextYearRevenueGoal() {
        this.nextYearRevenueGoalEditValue = this.metrics.nextYearRevenueGoal
        this.editingNextYearRevenueGoal = true
        this.shouldFocusNextYearRevenueGoal = true
    }

    public startEditFollowingYearRevenueGoal() {
        this.followingYearRevenueGoalEditValue = this.metrics.followingYearRevenueGoal
        this.editingFollowingYearRevenueGoal = true
        this.shouldFocusFollowingYearRevenueGoal = true
    }

    public startEditCurrentYearRevenueGoal() {
        this.currentYearRevenueGoalEditValue = this.metrics.currentYearRevenueGoal
        this.editingCurrentYearRevenueGoal = true
        this.shouldFocusCurrentYearRevenueGoal = true
    }

    public startEditCurrentQuarterGoal() {
        this.currentQuarterGoalEditValue = this.metrics.currentQuarterGoal
        this.editingCurrentQuarterGoal = true
        this.shouldFocusCurrentQuarterGoal = true
    }

    public cancelEditNextYearRevenueGoal() {
        this.editingNextYearRevenueGoal = false
        this.nextYearRevenueGoalEditValue = ''
    }

    public cancelEditFollowingYearRevenueGoal() {
        this.editingFollowingYearRevenueGoal = false
        this.followingYearRevenueGoalEditValue = ''
    }

    public cancelEditCurrentYearRevenueGoal() {
        this.editingCurrentYearRevenueGoal = false
        this.currentYearRevenueGoalEditValue = ''
    }

    public cancelEditCurrentQuarterGoal() {
        this.editingCurrentQuarterGoal = false
        this.currentQuarterGoalEditValue = ''
    }

    public saveNextYearRevenueGoal() {
        const dto: BidMetricUpdateDto = {
            name: `${this.metrics.nextYear}RevenueGoal`,
            value: this.nextYearRevenueGoalEditValue,
        }

        this.cancelableRequest(
            '',
            false,
            this.services.data.put<Metrics>('/api/BidDashboard/UpdateMetric', dto).subscribe({
                next: () => {
                    this.metrics.nextYearRevenueGoal = this.nextYearRevenueGoalEditValue
                    this.editingNextYearRevenueGoal = false
                    this.nextYearRevenueGoalEditValue = ''
                },
                error: error => {
                    console.error('Error updating next year revenue goal:', error)
                },
            }),
        )
    }

    public saveFollowingYearRevenueGoal() {
        const dto: BidMetricUpdateDto = {
            name: `${this.metrics.followingYear}RevenueGoal`,
            value: this.followingYearRevenueGoalEditValue,
        }

        this.cancelableRequest(
            '',
            false,
            this.services.data.put<Metrics>('/api/BidDashboard/UpdateMetric', dto).subscribe({
                next: () => {
                    this.metrics.followingYearRevenueGoal = this.followingYearRevenueGoalEditValue
                    this.editingFollowingYearRevenueGoal = false
                    this.followingYearRevenueGoalEditValue = ''
                },
                error: error => {
                    console.error('Error updating following year revenue goal:', error)
                },
            }),
        )
    }

    public saveCurrentYearRevenueGoal() {
        const dto: BidMetricUpdateDto = {
            name: `${this.metrics.currentYear}RevenueGoal`,
            value: this.currentYearRevenueGoalEditValue,
        }

        this.cancelableRequest(
            '',
            false,
            this.services.data.put<Metrics>('/api/BidDashboard/UpdateMetric', dto).subscribe({
                next: () => {
                    this.metrics.currentYearRevenueGoal = this.currentYearRevenueGoalEditValue
                    this.editingCurrentYearRevenueGoal = false
                    this.currentYearRevenueGoalEditValue = ''
                },
                error: error => {
                    console.error('Error updating current year revenue goal:', error)
                },
            }),
        )
    }

    public saveCurrentQuarterGoal() {
        const dto: BidMetricUpdateDto = {
            name: 'CurrentQuarterGoal',
            value: this.currentQuarterGoalEditValue,
        }

        this.cancelableRequest(
            '',
            false,
            this.services.data.put<Metrics>('/api/BidDashboard/UpdateMetric', dto).subscribe({
                next: () => {
                    this.metrics.currentQuarterGoal = this.currentQuarterGoalEditValue
                    this.editingCurrentQuarterGoal = false
                    this.currentQuarterGoalEditValue = ''
                },
                error: error => {
                    console.error('Error updating current quarter goal:', error)
                },
            }),
        )
    }
}
